// A Webflow "Embed" element whose HTML includes <script> tags (e.g. HubSpot forms).
// The HTML is server-rendered without its scripts, which are then executed in order on the
// client, each in its original position so scripts that use document.currentScript work.

import { useEffect, useMemo, useRef } from 'react';
import type { WfProps } from './element';

const SCRIPT_RE = /<script\b[^>]*>[\s\S]*?<\/script>/gi;

export function ScriptEmbed({ html, as: Tag = 'div', ...props }: WfProps & { html: string }) {
  const ref = useRef<HTMLElement>(null);
  // Scripts are swapped for placeholders so they can be re-created in place.
  const { scripts, staticHtml } = useMemo(() => {
    const found: string[] = [];
    const rest = html.replace(SCRIPT_RE, (s) => {
      found.push(s);
      return `<template data-script="${found.length - 1}"></template>`;
    });
    return { scripts: found, staticHtml: rest };
  }, [html]);

  useEffect(() => {
    const root = ref.current;
    // Run once per element, even under StrictMode's double-invoked effects.
    if (!root || root.dataset.scriptsRun) return;
    root.dataset.scriptsRun = 'true';
    void (async () => {
      for (let i = 0; i < scripts.length; i++) {
        const placeholder = root.querySelector(`template[data-script="${i}"]`);
        const parsed = new DOMParser().parseFromString(scripts[i]!, 'text/html').querySelector('script');
        if (!placeholder || !parsed) continue;
        const script = document.createElement('script');
        for (const attr of parsed.attributes) script.setAttribute(attr.name, attr.value);
        script.text = parsed.text;
        const loaded = script.src ? new Promise((r) => (script.onload = script.onerror = r)) : null;
        placeholder.replaceWith(script);
        if (loaded) await loaded;
      }
    })();
  }, [scripts]);

  return <Tag ref={ref} {...props} dangerouslySetInnerHTML={{ __html: staticHtml }} />;
}
